import React, { useState, useEffect } from 'react';
import { Fuel } from 'lucide-react';
import { SEO } from '../components/SEO';

interface FuelState {
  distance: string;
  fuelUsed: string;
  fuelPrice: string;
  currency: 'EUR' | 'BAM' | 'RSD';
  unit: 'km' | 'mi';
}

const currencySymbols: Record<string, string> = {
  EUR: '€',
  BAM: 'KM',
  RSD: 'RSD'
};

function KalkulatorPotrošnjeGoriva() {
  const [values, setValues] = useState<FuelState>({
    distance: '',
    fuelUsed: '',
    fuelPrice: '',
    currency: 'EUR',
    unit: 'km'
  });

  const [results, setResults] = useState<{
    consumption: number;
    costPerKm: number;
    totalCost: number;
  } | null>(null);

  const calculateConsumption = () => {
    const distance = parseFloat(values.distance);
    const fuelUsed = parseFloat(values.fuelUsed);
    const fuelPrice = parseFloat(values.fuelPrice);
    
    if (isNaN(distance) || isNaN(fuelUsed) || isNaN(fuelPrice)) {
      return null;
    }

    // Convert miles to kilometers if needed
    const distanceInKm = values.unit === 'mi' ? distance * 1.60934 : distance;

    // Calculate consumption (L/100km)
    const consumption = (fuelUsed / distanceInKm) * 100;
    
    // Calculate cost per kilometer
    const costPerKm = (fuelUsed * fuelPrice) / distanceInKm;
    
    // Calculate total cost
    const totalCost = fuelUsed * fuelPrice;

    return {
      consumption: parseFloat(consumption.toFixed(2)),
      costPerKm: parseFloat(costPerKm.toFixed(3)),
      totalCost: parseFloat(totalCost.toFixed(2))
    };
  };

  const handleInputChange = (field: keyof FuelState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    if (values.distance && values.fuelUsed && values.fuelPrice) {
      const results = calculateConsumption();
      setResults(results);
    }
  }, [values]);

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('hr-HR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const getEfficiencyRating = (consumption: number): string => {
    if (consumption <= 4) return 'Izvrsna';
    if (consumption <= 6) return 'Vrlo dobra';
    if (consumption <= 8) return 'Dobra';
    if (consumption <= 10) return 'Prosječna';
    if (consumption <= 12) return 'Povišena';
    return 'Visoka';
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Potrošnje Goriva",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte potrošnju goriva vašeg vozila i troškove po kilometru. Precizni izračuni s detaljnom analizom potrošnje.",
    "url": "https://online-kalkulator.com/kalkulator-potrosnje-goriva",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    }
  };

  return (
    <>
      <SEO 
        title="Kalkulator Potrošnje Goriva | Izračun Potrošnje i Troškova"
        description="Izračunajte potrošnju goriva vašeg vozila i troškove po kilometru. Precizni izračuni s detaljnom analizom potrošnje."
        canonicalUrl="/kalkulator-potrosnje-goriva"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Fuel className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Potrošnje Goriva</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prijeđena udaljenost
                  </label>
                  <div className="flex">
                    <input
                      type="number"
                      value={values.distance}
                      onChange={(e) => handleInputChange('distance', e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Unesite udaljenost"
                    />
                    <select
                      value={values.unit}
                      onChange={(e) => handleInputChange('unit', e.target.value as 'km' | 'mi')}
                      className="px-4 py-2 border border-l-0 border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                    >
                      <option value="km">km</option>
                      <option value="mi">mi</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Potrošeno gorivo (L)
                  </label>
                  <input
                    type="number"
                    value={values.fuelUsed}
                    onChange={(e) => handleInputChange('fuelUsed', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Unesite količinu goriva"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cijena goriva po litri
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={values.fuelPrice}
                      onChange={(e) => handleInputChange('fuelPrice', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Unesite cijenu"
                    />
                    <select
                      value={values.currency}
                      onChange={(e) => handleInputChange('currency', e.target.value as FuelState['currency'])}
                      className="absolute right-1 top-1 px-2 py-1 bg-gray-50 border border-gray-300 rounded"
                    >
                      <option value="EUR">€</option>
                      <option value="BAM">KM</option>
                      <option value="RSD">RSD</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Results */}
              {results && (
                <div className="space-y-4 border-t pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Potrošnja</div>
                      <div className="text-xl font-bold text-blue-600">{results.consumption} L/100km</div>
                      <div className="text-sm text-gray-500">{getEfficiencyRating(results.consumption)} potrošnja</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Trošak po km</div>
                      <div className="text-xl font-bold text-blue-600">
                        {formatNumber(results.costPerKm)} {currencySymbols[values.currency]}
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Ukupni trošak</div>
                      <div className="text-xl font-bold text-blue-600">
                        {formatNumber(results.totalCost)} {currencySymbols[values.currency]}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Potrošnje Goriva</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator potrošnje goriva je koristan alat koji vam pomaže pratiti i analizirati
                  potrošnju goriva vašeg vozila. Izračunava prosječnu potrošnju, trošak po kilometru
                  i ukupne troškove goriva za određenu relaciju.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Izračunati Potrošnju</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite prijeđenu udaljenost u kilometrima ili miljama</li>
                  <li>Unesite količinu potrošenog goriva u litrama</li>
                  <li>Unesite trenutnu cijenu goriva po litri</li>
                  <li>Odaberite željenu valutu za prikaz troškova</li>
                  <li>Rezultati će se automatski izračunati</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kategorije Potrošnje</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                      <span className="font-medium">Izvrsna:</span>
                      <span>≤ 4 L/100km</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                      <span className="font-medium">Vrlo dobra:</span>
                      <span>4-6 L/100km</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-yellow-50 rounded">
                      <span className="font-medium">Dobra:</span>
                      <span>6-8 L/100km</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center p-2 bg-yellow-50 rounded">
                      <span className="font-medium">Prosječna:</span>
                      <span>8-10 L/100km</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                      <span className="font-medium">Povišena:</span>
                      <span>10-12 L/100km</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                      <span className="font-medium">Visoka:</span>
                      <span>&gt; 12 L/100km</span>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Savjeti za Smanjenje Potrošnje</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Održavajte konstantnu brzinu</li>
                  <li>Izbjegavajte nagla ubrzanja i kočenja</li>
                  <li>Redovito održavajte vozilo</li>
                  <li>Provjeravajte tlak u gumama</li>
                  <li>Uklonite nepotreban teret iz vozila</li>
                  <li>Koristite preporučeno ulje i gorivo</li>
                  <li>Planirajte putovanja unaprijed</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Faktori koji Utječu na Potrošnju</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Način vožnje i stil vožnje</li>
                  <li>Stanje i održavanje vozila</li>
                  <li>Vrsta terena i uvjeti na cesti</li>
                  <li>Vremenski uvjeti</li>
                  <li>Opterećenje vozila</li>
                  <li>Korištenje klima uređaja</li>
                  <li>Aerodinamika vozila</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Rezultati su informativne prirode</li>
                  <li>Stvarna potrošnja može varirati</li>
                  <li>Redovito pratite potrošnju za točnije podatke</li>
                  <li>Uspoređujte potrošnju u sličnim uvjetima</li>
                  <li>Vodite evidenciju o punjenju goriva</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorPotrošnjeGoriva;